import React, { ComponentPropsWithRef, FC, FormEvent, PropsWithChildren, forwardRef, useState } from 'react';
import { useFormState } from '@form/business/useFormState.hook';
import { FullPageLoader } from '@layout/components/FullPageLoader/FullPageLoader.component';
import { ConfirmationModal } from '@layout/components/Modal/ConfirmationModal.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { AxiosError } from 'axios';

type Props = Omit<ComponentPropsWithRef<'form'>, 'onSubmit'> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmitCallback: (e?: FormEvent<HTMLFormElement>) => Promise<any>;
  setIsSubmitted: ReturnType<typeof useFormState>['plcFormProps']['setIsSubmitted'];
  setIsDirty: ReturnType<typeof useFormState>['plcFormProps']['setIsDirty'];
  setIsFormLoading: ReturnType<typeof useFormState>['plcFormProps']['setIsFormLoading'];
  scrollToFirstError?: ReturnType<typeof useFormState>['plcFormProps']['scrollToFirstError'];
  isFormValid: ReturnType<typeof useFormState>['plcFormProps']['isFormValid'];
  isFormLoading?: ReturnType<typeof useFormState>['plcFormProps']['isFormLoading'];
  setSubmitCallbackErrorCode: ReturnType<typeof useFormState>['plcFormProps']['setSubmitCallbackErrorCode'];
  setSubmitCallbackSuccess: ReturnType<typeof useFormState>['plcFormProps']['setSubmitCallbackSuccess'];
  resetMessage: ReturnType<typeof useFormState>['plcFormProps']['resetMessage'];
  useConfirmationModal?: boolean;
  confirmationModalTitleI18n?: string;
  confirmationModalBodyI18n?: string;
  withLoader?: boolean;
};

const PlcForm: FC<PropsWithChildren<Props>> = forwardRef(
  (
    {
      noValidate = true,
      onSubmitCallback,
      setIsSubmitted,
      scrollToFirstError,
      setIsDirty,
      setIsFormLoading,
      isFormValid,
      isFormLoading,
      setSubmitCallbackErrorCode,
      setSubmitCallbackSuccess,
      resetMessage,
      children,
      useConfirmationModal,
      confirmationModalBodyI18n,
      confirmationModalTitleI18n,
      withLoader = true,
      ...rest
    },
    formRef
  ) => {
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const { t } = useTranslation(['common']);

    function callCallback(event?: FormEvent<HTMLFormElement>) {
      onSubmitCallback(event)
        .then(r => {
          setSubmitCallbackSuccess(true);
          setIsFormLoading(false);
          return r;
        })
        .catch((callbackError: AxiosError) => {
          setSubmitCallbackErrorCode(callbackError?.response?.status || 500);
          setIsFormLoading(false);
        });
    }

    function handleOnSubmit(event: FormEvent<HTMLFormElement>) {
      event.preventDefault();
      resetMessage();
      setIsSubmitted(true);
      if (!isFormValid) {
        if (scrollToFirstError) scrollToFirstError();
        return;
      }
      setIsDirty(false);
      setIsFormLoading(true);
      if (useConfirmationModal) {
        setConfirmationModalOpen(true);
      } else {
        callCallback(event);
      }
    }

    function handleConfirm() {
      setConfirmationModalOpen(false);
      callCallback();
    }

    function handleDecline() {
      setConfirmationModalOpen(false);
      setIsFormLoading(false);
    }

    return (
      <>
        <form ref={formRef} noValidate={noValidate} onSubmit={handleOnSubmit} {...rest}>
          {children}
          {withLoader && isFormLoading && !confirmationModalOpen && <FullPageLoader visible={isFormLoading} />}
        </form>
        {useConfirmationModal && (
          <ConfirmationModal
            onConfirm={handleConfirm}
            onDecline={handleDecline}
            title={confirmationModalTitleI18n ? t(confirmationModalTitleI18n) : undefined}
            visible={confirmationModalOpen}
          >
            {confirmationModalBodyI18n ? t(confirmationModalBodyI18n) : undefined}
          </ConfirmationModal>
        )}
      </>
    );
  }
);

export { PlcForm };
